import {
  CalendarDays,
  CheckCircle2,
  Clock,
  Flame,
  Lightbulb,
  Sparkles,
  Target,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { ReportActionsPanel, type ReportAction } from "@/components/report-actions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { formatFullDate } from "@/lib/relative-date";

/**
 * Rendering for a stored progress report, and for the meter that stands in until
 * one is available.
 *
 * Every figure on screen comes from `report.stats` — the snapshot frozen when the
 * report was written — and never from a number appearing inside the prose. The two
 * are separated on the server for exactly this reason: a sentence is a sentence,
 * and only the snapshot is data.
 */

type Item = Record<string, string | undefined>;

type Report = {
  id: string;
  audience: string;
  subjectName?: string | null;
  sections: Record<string, unknown>;
  stats: Record<string, any>;
  sessionCount: number;
  coveredFrom?: string | null;
  coveredThrough?: string | null;
  createdAt: string;
  actions?: ReportAction[] | null;
};

type Availability = {
  allowed: boolean;
  blockedBy?: string | null;
  reason?: string | null;
  requestedBy?: string;
  subjectName?: string | null;
  sessionsDone: number;
  sessionsNeeded: number;
  sessionsLeft?: number;
  sessionProgress?: number;
  daysLeft?: number;
  availableAt?: string | null;
  lastReportAt?: string | null;
};

function items(value: unknown): Item[] {
  return Array.isArray(value) ? (value as Item[]) : [];
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** The numbers, from the snapshot. Nothing here is the AI's arithmetic. */
export function ReportStats({ report }: { report: Report }) {
  const totals = report.stats?.totals ?? {};
  const points = report.stats?.points ?? {};

  const cells = [
    { label: "Sesijos", value: report.sessionCount, icon: CalendarDays },
    { label: "Teisingai", value: totals.correct, icon: CheckCircle2 },
    { label: "Tikslumas", value: totals.accuracy != null ? `${totals.accuracy}%` : null, icon: TrendingUp },
    { label: "Laikas", value: totals.minutes != null ? `${totals.minutes} min.` : null, icon: Clock },
    { label: "Taškai", value: points.window, icon: Sparkles },
  ].filter((cell) => cell.value != null);

  if (cells.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {cells.map(({ label, value, icon: Icon }) => (
        <div key={label} className="rounded-xl border bg-card/60 p-3 text-center">
          <Icon className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
          <div className="text-lg font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      ))}
    </div>
  );
}

/** Which stretch of work the report describes. */
export function ReportWindow({ report }: { report: Report }) {
  const from = report.coveredFrom;
  const through = report.coveredThrough ?? report.createdAt;

  return (
    <p className="text-xs text-muted-foreground">
      {from ? `${formatFullDate(from)} – ${formatFullDate(through)}` : `Iki ${formatFullDate(through)}`}
      {report.subjectName ? ` · ${report.subjectName}` : ""}
    </p>
  );
}

/**
 * The learner's report. Celebration first and by name; the one suggestion sits at
 * the end, worded as an invitation rather than a task — the same rule the tutor
 * follows, where nothing tells a child what they do not know.
 */
function StudentReport({ report }: { report: Report }) {
  const s = report.sections;
  const wins = items(s.wins);

  return (
    <div className="space-y-6">
      {text(s.greeting) && (
        <p className="text-xl font-semibold leading-snug sm:text-2xl">{text(s.greeting)}</p>
      )}

      {wins.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {wins.map((win, i) => (
            <div
              key={i}
              className="rounded-2xl border bg-gradient-to-br from-amber-500/10 to-pink-500/10 p-4"
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="text-lg">{["🌟", "🚀", "🏅", "💡"][i % 4]}</span>
                <span className="font-semibold">{win.title}</span>
              </div>
              {win.detail && <p className="text-sm text-muted-foreground">{win.detail}</p>}
            </div>
          ))}
        </div>
      )}

      {text(s.growth) && (
        <div className="rounded-2xl border bg-card p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            Kas pasistūmėjo
          </div>
          <p className="text-sm leading-relaxed">{text(s.growth)}</p>
        </div>
      )}

      {text(s.next_step) && (
        <div className="rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
            <Target className="h-4 w-4 text-primary" />
            Ką smagu pabandyti
          </div>
          <p className="text-sm leading-relaxed">{text(s.next_step)}</p>
        </div>
      )}

      {text(s.closing) && (
        <p className="text-center text-base font-medium text-muted-foreground">{text(s.closing)}</p>
      )}
    </div>
  );
}

/**
 * The guardian's report. Same window, opposite job: what to celebrate is stated
 * as briefly as it is honestly, and the space goes to what is going badly and what
 * to do about it tomorrow.
 */
function GuardianReport({ report }: { report: Report }) {
  const s = report.sections;
  const focus = (Array.isArray(s.focus) ? s.focus : []).filter(
    (item): item is string => typeof item === "string" && item.trim().length > 0
  );

  return (
    <div className="space-y-5">
      {text(s.summary) && <p className="text-sm leading-relaxed sm:text-base">{text(s.summary)}</p>}

      {/*
        Absent on a first report, and that is the honest state rather than a gap:
        there is nothing to compare against yet. Given its own framed block because
        it is the one paragraph a returning reader looks for first — did the thing
        we were told about last time get better?
      */}
      {text(s.since_last) && (
        <div className="rounded-2xl border bg-muted/40 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
            <TrendingUp className="h-4 w-4 text-sky-500" />
            Nuo praėjusios ataskaitos
          </div>
          <p className="text-sm leading-relaxed">{text(s.since_last)}</p>
        </div>
      )}

      {focus.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Artimiausias fokusas:</span>
          {focus.map((item, i) => (
            <Badge key={i} variant="secondary">
              {item}
            </Badge>
          ))}
        </div>
      )}

      <Section
        title="Ką pagirti"
        icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
        rows={items(s.celebrate).map((row) => ({ head: row.title, body: row.detail }))}
      />

      <Section
        title="Kur sunkumai"
        icon={<TriangleAlert className="h-4 w-4 text-amber-500" />}
        rows={items(s.struggles).map((row) => ({
          head: row.skill,
          body: row.what,
          // The evidence is what separates a finding from an opinion, so it is
          // shown rather than folded into the sentence.
          note: row.evidence,
        }))}
      />

      <Section
        title="Ką daryti"
        icon={<Lightbulb className="h-4 w-4 text-primary" />}
        rows={items(s.suggestions).map((row) => ({ head: row.action, body: row.why }))}
        numbered
      />

      {/*
        Last, and after the advice rather than instead of it. The two are different
        kinds of answer — „Ką daryti" is for the kitchen table, this is what the app
        will do differently — and reading what is wrong before what to press is the
        order a decision actually gets made in.
      */}
      <ReportActionsPanel actions={report.actions} />
    </div>
  );
}

function Section({
  title,
  icon,
  rows,
  numbered = false,
}: {
  title: string;
  icon: React.ReactNode;
  rows: { head?: string; body?: string; note?: string }[];
  numbered?: boolean;
}) {
  if (rows.length === 0) return null;

  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
      </div>
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="flex gap-3">
            {numbered && (
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {i + 1}
              </span>
            )}
            <div className="min-w-0 space-y-0.5">
              {row.head && <p className="text-sm font-medium">{row.head}</p>}
              {row.body && <p className="text-sm text-muted-foreground">{row.body}</p>}
              {row.note && (
                <p className="text-xs text-muted-foreground/80">
                  <span className="font-medium">Iš kur matosi: </span>
                  {row.note}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * `"teacher"` is the value this column held before 2026-08-10 and is still read as
 * the grown-up's report: rows were migrated, but a cached Apollo entry or an open
 * tab can still hand us the old string, and falling through to the child's layout
 * would render a diagnosis as a letter — the wrong sections, in the wrong voice.
 */
const GUARDIAN_AUDIENCES = ["guardian", "teacher"];

export function ProgressReportView({ report }: { report: Report }) {
  return GUARDIAN_AUDIENCES.includes(report.audience ?? "") ? (
    <GuardianReport report={report} />
  ) : (
    <StudentReport report={report} />
  );
}

/**
 * Progress until the report is available — the whole reason a learner does not
 * simply meet a disabled button.
 *
 * Both halves are always shown, because both are true: the sessions they can add
 * to today, and the week that has to pass. Hiding the satisfied one would make a
 * finished meter sit next to a refusal with nothing explaining it.
 */
export function ReportAvailabilityMeter({ availability }: { availability: Availability }) {
  const needed = availability.sessionsNeeded ?? 0;
  const done = Math.min(availability.sessionsDone ?? 0, needed);
  const sessionsDone = done >= needed;
  const waiting = (availability.daysLeft ?? 0) > 0;

  if (availability.allowed) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
        <Sparkles className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <span className="font-medium">Nauja ataskaita paruošta rengti!</span>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border bg-card/60 p-4">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 font-medium">
            <Flame className="h-4 w-4 text-orange-500" />
            {availability.subjectName ? `${availability.subjectName} sesijos` : "Sesijos"}
          </span>
          <span className={sessionsDone ? "font-semibold text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}>
            {done} / {needed}
          </span>
        </div>
        <Progress value={needed > 0 ? (done / needed) * 100 : 0} className="h-2" />
        <p className="text-xs text-muted-foreground">
          {sessionsDone
            ? "Sesijų jau pakanka"
            : `Dar ${needed - done} tos pačios temos ${needed - done === 1 ? "sesija" : "sesijos"}`}
        </p>
      </div>

      {availability.lastReportAt && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 font-medium">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              Savaitė nuo praėjusios
            </span>
            <span className={waiting ? "text-muted-foreground" : "font-semibold text-emerald-600 dark:text-emerald-400"}>
              {waiting ? `dar ${availability.daysLeft} d.` : "praėjo"}
            </span>
          </div>
          <Progress
            value={Math.min(100, ((7 - (availability.daysLeft ?? 0)) / 7) * 100)}
            className="h-2"
          />
        </div>
      )}
    </div>
  );
}

/** A stored report in a card, with its window and its numbers. */
export function ProgressReportCard({
  report,
  title,
  children,
}: {
  report: Report;
  title?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">
              {title ??
                (GUARDIAN_AUDIENCES.includes(report.audience ?? "")
                  ? "Pažangos ataskaita"
                  : "Vaiko laiškas")}
            </CardTitle>
            <ReportWindow report={report} />
          </div>
          {children}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <ReportStats report={report} />
        <ProgressReportView report={report} />
      </CardContent>
    </Card>
  );
}
